import { useState } from 'react';
import { Card, Typography, Table, Tag, Button, Space, Modal, Textarea, Descriptions, Select, Row, Col, Statistic, message } from '../../ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { formatIstDate } from '../../lib/time';

const STATUS_COLOR: Record<string, 'gold' | 'green' | 'red'> = { PENDING: 'gold', APPROVED: 'green', REJECTED: 'red' };
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * After-billing prompt-payment Cash Discount — approval queue. Proposals arrive PENDING; an approver
 * (Accounts/Admin/SA) posts the ledger credit only with mandatory audit remarks. The Tally invoice is
 * never modified.
 */
export function DiscountAdjustmentsPage() {
  const [status, setStatus] = useState<string>('PENDING');
  const [approveFor, setApproveFor] = useState<any>(null);
  const [rejectFor, setRejectFor] = useState<any>(null);
  const canApprove = usePermission('DISCOUNT.ADJUST.APPROVE');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['discount-adjustments', status],
    queryFn: () => request<any[]>({ url: '/discount-adjustments', params: status ? { status } : {} }),
  });
  const rows = data ?? [];
  const pendingAmt = rows.filter((r: any) => r.status === 'PENDING').reduce((s: number, r: any) => s + Number(r.amount || 0), 0);

  const columns = [
    { title: 'Adj No', dataIndex: 'adjNo' },
    { title: 'Dealer', dataIndex: 'retailerName', render: (v: any, r: any) => v ?? `#${r.retailerId}` },
    { title: 'Invoice', dataIndex: 'invoiceNo', render: (v: any) => v ?? '—' },
    { title: 'Basis', dataIndex: 'basisAmount', align: 'right' as const, render: (v: any) => inr2(v) },
    { title: '%', dataIndex: 'pct', align: 'right' as const, render: (v: any) => `${Number(v)}%` },
    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (v: any) => <b>{inr2(v)}</b> },
    { title: 'Paid on', dataIndex: 'paymentDate', render: (v: any, r: any) => v ? <span>{formatIstDate(v)} <Tag>{r.daysFromInvoice}d</Tag></span> : '—' },
    { title: 'Status', dataIndex: 'status', render: (v: string, r: any) => <Space size={4}><Tag tone={STATUS_COLOR[v]}>{v}</Tag>{r.isOverride ? <Tag tone="red">OVERRIDE</Tag> : null}</Space> },
    { title: 'By', dataIndex: 'createdByName', render: (v: any, r: any) => r.status === 'APPROVED' ? `✓ ${r.approvedByName ?? ''}` : (v ?? '—') },
    ...(canApprove ? [{
      title: '', width: 150,
      render: (_: any, r: any) => r.status === 'PENDING' ? (
        <Space size={4} onClick={(e: any) => e.stopPropagation()}>
          <Button size="sm" variant="primary" icon={<CheckCircle2 size={16} />} onClick={() => setApproveFor(r)}>Approve</Button>
          <Button size="sm" variant="danger" icon={<XCircle size={16} />} onClick={() => setRejectFor(r)} aria-label="Reject" />
        </Space>
      ) : null,
    }] : []),
  ];

  return (
    <div>
      <Typography.Title level={4}>Discount Adjustments</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        After-billing prompt-payment cash discount. Posts an internal ledger credit only after approval with audit remarks — the tax invoice is never changed.
      </Typography.Paragraph>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Pending" value={rows.filter((r: any) => r.status === 'PENDING').length} suffix={`· ${inr2(pendingAmt)}`} /></Card></Col>
      </Row>
      <Card>
        <Space style={{ marginBottom: 12 }}>
          <span>Status:</span>
          <Select value={status} onChange={setStatus} className="w-40"
            options={[{ value: '', label: 'All' }, { value: 'PENDING', label: 'Pending' }, { value: 'APPROVED', label: 'Approved' }, { value: 'REJECTED', label: 'Rejected' }]} />
        </Space>
        <Table rowKey="id" loading={isLoading} dataSource={rows} columns={columns as any} size="small" pagination={{ pageSize: 25 }}
          expandable={{
            expandedRowRender: (r: any) => (
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Rule / calculation" span={2}>{r.ruleSource ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Remarks" span={2}>{r.remarks ?? (r.status === 'REJECTED' ? r.rejectedReason : '—')}</Descriptions.Item>
                <Descriptions.Item label="Ledger ref">{r.ledgerRefId ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Scheme">#{r.schemeId ?? '—'}</Descriptions.Item>
              </Descriptions>
            ),
          }} />
      </Card>

      <ApproveModal adj={approveFor} onClose={() => setApproveFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ['discount-adjustments'] })} />
      <RejectModal adj={rejectFor} onClose={() => setRejectFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ['discount-adjustments'] })} />
    </div>
  );
}

function ApproveModal({ adj, onClose, onDone }: { adj: any; onClose: () => void; onDone: () => void }) {
  const [remarks, setRemarks] = useState('');
  const m = useMutation({
    mutationFn: () => request({ url: `/discount-adjustments/${adj.id}/approve`, method: 'POST', data: { remarks } }),
    onSuccess: () => { message.success('Approved — ledger credit posted'); setRemarks(''); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={!!adj} onOpenChange={(o) => { if (!o) { setRemarks(''); onClose(); } }} title={adj ? `Approve ${adj.adjNo}` : ''}
      footer={<><Button variant="default" onClick={() => { setRemarks(''); onClose(); }}>Cancel</Button><Button variant="primary" disabled={remarks.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Approve & post credit</Button></>}>
      {adj && (
        <>
          <Descriptions size="small" column={1} bordered className="mb-3">
            <Descriptions.Item label="Dealer">{adj.retailerName ?? `#${adj.retailerId}`}</Descriptions.Item>
            <Descriptions.Item label="Invoice">{adj.invoiceNo ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Discount">{Number(adj.pct)}% of {inr2(adj.basisAmount)} = <b>{inr2(adj.amount)}</b></Descriptions.Item>
            <Descriptions.Item label="Basis">Paid {adj.daysFromInvoice}d after invoice → {adj.ruleSource}</Descriptions.Item>
          </Descriptions>
          <Typography.Text type="secondary">This posts a ledger credit of <b>{inr2(adj.amount)}</b> reducing the dealer's outstanding. The tax invoice is not changed.</Typography.Text>
          <Textarea rows={3} maxLength={500} style={{ marginTop: 10 }} placeholder="Audit remarks (required) — e.g. verified UPI UTR, payment within 15 days" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </>
      )}
    </Modal>
  );
}

function RejectModal({ adj, onClose, onDone }: { adj: any; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => request({ url: `/discount-adjustments/${adj.id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Rejected'); setReason(''); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={!!adj} onOpenChange={(o) => { if (!o) { setReason(''); onClose(); } }} title={adj ? `Reject ${adj.adjNo}` : ''}
      footer={<><Button variant="default" onClick={() => { setReason(''); onClose(); }}>Cancel</Button><Button variant="danger" disabled={reason.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Reject</Button></>}>
      <Textarea rows={3} maxLength={255} placeholder="Reason for rejection" value={reason} onChange={(e) => setReason(e.target.value)} />
    </Modal>
  );
}
